// Working Pattern Display Component - Compact Table View

interface WorkingDay {
  day: string;
  dayShort: string;
  isWorking: boolean;
  hours: number;
}

interface WorkingPatternDisplayProps {
  workingDays?: WorkingDay[];
}

export function WorkingPatternDisplay({
  workingDays,
}: WorkingPatternDisplayProps) {
  // Default working pattern if not provided
  const defaultWorkingDays: WorkingDay[] = [
    { day: 'Monday', dayShort: 'Mon', isWorking: true, hours: 8 },
    { day: 'Tuesday', dayShort: 'Tue', isWorking: true, hours: 8 },
    { day: 'Wednesday', dayShort: 'Wed', isWorking: true, hours: 8 },
    { day: 'Thursday', dayShort: 'Thu', isWorking: true, hours: 8 },
    { day: 'Friday', dayShort: 'Fri', isWorking: true, hours: 8 },
    { day: 'Saturday', dayShort: 'Sat', isWorking: false, hours: 0 },
    { day: 'Sunday', dayShort: 'Sun', isWorking: false, hours: 0 },
  ];

  const days = workingDays || defaultWorkingDays;

  // Calculate statistics from actual working days
  const totalHours = parseFloat(days.reduce((sum, day) => sum + day.hours, 0).toFixed(1));
  const workingDaysCount = days.filter((d) => d.isWorking).length;
  const averageHours = workingDaysCount > 0 ? totalHours / workingDaysCount : 0;

  return (
    <div className="space-y-3">
      {/* Days Grid - Desktop */}
      <div className="hidden sm:grid sm:grid-cols-7 gap-2">
        {days.map((day) => (
          <div
            key={day.day}
            className="flex flex-col items-center justify-center p-2 rounded-md border bg-card"
          >
            <div className="text-xs font-medium text-muted-foreground mb-1">
              {day.dayShort}
            </div>
            <div className="flex items-center gap-1">
              {day.isWorking ? (
                <>
                  <span className="text-green-600 dark:text-green-400 font-semibold">
                    ✓
                  </span>
                  <span className="text-sm font-medium">{parseFloat(day.hours.toFixed(1))}h</span>
                </>
              ) : (
                <>
                  <span className="text-muted-foreground">-</span>
                  <span className="text-sm text-muted-foreground">
                    {parseFloat(day.hours.toFixed(1))}h
                  </span>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Days Grid - Mobile (Stacked) */}
      <div className="sm:hidden space-y-2">
        {/* Weekdays */}
        <div className="grid grid-cols-5 gap-2">
          {days.slice(0, 5).map((day) => (
            <div
              key={day.day}
              className="flex flex-col items-center justify-center p-2 rounded-md border bg-card"
            >
              <div className="text-xs font-medium text-muted-foreground mb-1">
                {day.dayShort}
              </div>
              <div className="flex items-center gap-1">
                {day.isWorking ? (
                  <>
                    <span className="text-green-600 dark:text-green-400 font-semibold">
                      ✓
                    </span>
                    <span className="text-sm font-medium">{parseFloat(day.hours.toFixed(1))}h</span>
                  </>
                ) : (
                  <>
                    <span className="text-muted-foreground">-</span>
                    <span className="text-sm text-muted-foreground">
                      {parseFloat(day.hours.toFixed(1))}h
                    </span>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Weekend */}
        <div className="grid grid-cols-2 gap-2">
          {days.slice(5, 7).map((day) => (
            <div
              key={day.day}
              className="flex flex-col items-center justify-center p-2 rounded-md border bg-card"
            >
              <div className="text-xs font-medium text-muted-foreground mb-1">
                {day.dayShort}
              </div>
              <div className="flex items-center gap-1">
                {day.isWorking ? (
                  <>
                    <span className="text-green-600 dark:text-green-400 font-semibold">
                      ✓
                    </span>
                    <span className="text-sm font-medium">{parseFloat(day.hours.toFixed(1))}h</span>
                  </>
                ) : (
                  <>
                    <span className="text-muted-foreground">-</span>
                    <span className="text-sm text-muted-foreground">
                      {parseFloat(day.hours.toFixed(1))}h
                    </span>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground bg-muted/50 rounded-md p-2">
        <span className="font-medium">{workingDaysCount} working days</span>
        <span>•</span>
        <span className="font-medium">{totalHours}h total</span>
        <span>•</span>
        <span className="font-medium">{averageHours.toFixed(1)}h average</span>
      </div>
    </div>
  );
}
